/* eslint-disable no-console */
/**
 * Debug utilities for development environments
 */

// Debug levels for different components
export enum DebugLevel {
  DEBUG = 4,
  ERROR = 1,
  INFO = 3,
  NONE = 0,
  TRACE = 5,
  WARN = 2,
}

// Debug configuration
type DebugConfig = {
  enabled: boolean;
  level: DebugLevel;
  logToConsole: boolean;
};

// Default configuration
const defaultConfig: DebugConfig = {
  enabled: import.meta.env['VITE_APP_DEBUG_EDGE'] === 'true',
  level: DebugLevel.DEBUG,
  logToConsole: true,
};

/**
 * Creates a debugger instance for a specific module
 * @param module Name of the module/component being debugged
 * @param config Optional configuration to override defaults
 * @returns Debug functions for different levels
 */
export function createDebugger(
  module: string,
  config: Partial<DebugConfig> = {},
) {
  const mergedConfig: DebugConfig = { ...defaultConfig, ...config };

  // Only enable in development or when explicitly enabled
  const isEnabled =
    mergedConfig.enabled ||
    import.meta.env.MODE === 'development' ||
    import.meta.env.MODE === 'localmode';

  // Base logging function
  const log = (level: DebugLevel, ...args: unknown[]) => {
    if (!isEnabled || level > mergedConfig.level) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}][${module}][${DebugLevel[level]}]`;

    if (mergedConfig.logToConsole) {
      switch (level) {
        case DebugLevel.ERROR: {
          console.error(prefix, ...args);
          break;
        }
        case DebugLevel.WARN: {
          console.warn(prefix, ...args);
          break;
        }
        case DebugLevel.INFO: {
          console.info(prefix, ...args);
          break;
        }
        default: {
          console.log(prefix, ...args);
        }
      }
    }

    // Add any custom logging targets here (storage, network, etc.)
  };

  return {
    debug: (...args: unknown[]) => log(DebugLevel.DEBUG, ...args),
    error: (...args: unknown[]) => log(DebugLevel.ERROR, ...args),
    info: (...args: unknown[]) => log(DebugLevel.INFO, ...args),
    trace: (...args: unknown[]) => log(DebugLevel.TRACE, ...args),
    warn: (...args: unknown[]) => log(DebugLevel.WARN, ...args),
  };
}

/**
 * Edge-specific debug logger
 */
export const edgeDebug = createDebugger('Edge');
